
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  PhotoIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import { getImageUrlWithEnv } from '../utils/imageUrl';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const PhotoManagement = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedViews, setUpdatedViews] = useState(new Set());
  const navigate = useNavigate();

  const categories = ['მატჩები', 'ტრენინგი', 'ღონისძიებები', 'ფეხბურთელები', 'გულშემატკივრები', 'სტადიონი'];

  useEffect(() => {
    loadPhotos();
    
    // Set up periodic polling to refresh view counts every 15 seconds
    const interval = setInterval(() => {
      loadPhotos(false);
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);

  const loadPhotos = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const response = await fetch(getApiUrl('/api/photos/admin'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const photosData = await response.json();
        
        // Check for view count changes and highlight them
        if (!showLoader && photos.length > 0) {
          const newUpdatedViews = new Set();
          photosData.forEach(newPhoto => {
            const oldPhoto = photos.find(p => p.id === newPhoto.id);
            if (oldPhoto && oldPhoto.views !== newPhoto.views) {
              newUpdatedViews.add(newPhoto.id);
            }
          });
          setUpdatedViews(newUpdatedViews);
          // Clear highlights after 5 seconds
          setTimeout(() => setUpdatedViews(new Set()), 5000);
        }
        
        setPhotos(photosData);
      } else {
        console.error('Error loading photos:', response.statusText);
      }
      if (showLoader) setLoading(false);
    } catch (error) {
      console.error('Error loading photos:', error);
      if (showLoader) setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ნამდვილად გსურთ ამ ფოტოს წაშლა?')) {
      try {
        // Get token from the same source as apiService
        const storedUser = localStorage.getItem('fciveria_admin_user');
        const token = storedUser ? JSON.parse(storedUser).token : null;
        
        if (!token) {
          console.error('No auth token found');
          return;
        }
        
        const response = await fetch(getApiUrl(`/api/photos/${id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          loadPhotos();
        } else {
          console.error('Error deleting photo:', response.statusText);
        }
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    }
  };

  const togglePublish = async (photo) => {
    try {
      // Get token from the same source as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      const response = await fetch(getApiUrl(`/api/photos/${photo.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...photo,
          isPublished: !photo.isPublished
        })
      });
      
      if (response.ok) {
        loadPhotos();
      } else {
        console.error('Error updating photo:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating photo:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title="ფოტო გალერეის მართვა"
        subtitle="მართეთ ფოტო კონტენტი"
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/photos/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          ახალი ფოტო
        </button>
      </AdminHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {photos.length === 0 ? (
            <div className="text-center py-12">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 font-georgian">ფოტოები არ არის</h3>
              <p className="mt-1 text-sm text-gray-500 font-georgian">დაიწყეთ ახალი ფოტოს დამატებით</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/_panel-7c39f8/photos/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 font-georgian"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  ახალი ფოტო
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
              {photos.map((photo) => (
                <div key={photo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="relative">
                    <img
                      src={getImageUrlWithEnv(photo.imageUrl)}
                      alt={photo.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-2 right-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        photo.isPublished 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {photo.isPublished ? 'გამოქვეყნებული' : 'არ არის გამოქვეყნებული'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 font-georgian mb-2 line-clamp-2">
                      {photo.title}
                    </h3>
                    <p className="text-xs text-gray-600 font-georgian mb-2 line-clamp-2">
                      {photo.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 font-georgian mb-3">
                      <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded">
                        {photo.category}
                      </span>
                    <span className={`flex items-center transition-all duration-500 ${
                      updatedViews.has(photo.id) 
                        ? 'text-green-600 font-bold scale-110' 
                        : ''
                    }`}>
                      <EyeIcon className="h-3 w-3 mr-1" />
                      {photo.views}
                    </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => togglePublish(photo)}
                        className={`text-xs px-3 py-1 rounded font-georgian ${
                          photo.isPublished
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-green-100 text-green-600 hover:bg-green-200'
                        }`}
                      >
                        {photo.isPublished ? 'დამალვა' : 'გამოქვეყნება'}
                      </button>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/_panel-7c39f8/photos/${photo.id}/edit`)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(photo.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <PhotoIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">სულ ფოტოები</p>
                <p className="text-2xl font-bold text-gray-900">{photos.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">გამოქვეყნებული</p>
                <p className="text-2xl font-bold text-gray-900">
                  {photos.filter(p => p.isPublished).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">#</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">კატეგორიები</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-yellow-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">👁</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 font-georgian">სულ ნახვები</p>
                <p className="text-2xl font-bold text-gray-900">
                  {photos.reduce((sum, p) => sum + p.views, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoManagement;
